import React from 'react';
import mapboxgl from 'mapbox-gl';
import Plot from 'react-plotly.js';
import * as ReportObjects from './ReportObjects';
import { overlayDefintions } from './MapOverlays';
import { LatLon } from './Navigation';
import { generate_pdf, all_pages, setAllPages, map_conversion_handler } from './DownloadPDF';
import * as G from './General';
import * as arc from './Arc.js';
import * as turf from '@turf/turf';
import {logReportUsage} from './UsageLog.js';
export var buff_line_by = 10;

function normaliseZoneName(name) {
	if (typeof name !== "string") return "";
	return name.trim().toLowerCase();
}

function tidyZoneLabel(name) {
	if (typeof name !== "string") return "";
	return name.replace(/EXCLUSIVE ECONOMIC ZONE/gi, "EEZ");
}

const report_data: ReportObjects.report_info = {
    transit: "",
    vessel: "",
    route: [],
    risk: {
        risks: [],
        incidents_types: [],
        incident_likelihood: []
    },
    speed_freeboard: [],
    hours_in: []
};

export function data_extraction (data: ReportObjects.AnalysedRoute) {
    report_data.route = data.waypoints;
    report_data.transit = {
        etd: data.etd,
        eta: data.eta,
        hours: data.total_hours,
        max_speed: data.max_speed,
        min_speed: data.min_speed,
        distance: data.total_distance,
        max_freeboard: data.max_freeboard,
        min_freeboard: data.min_freeboard
    };
    report_data.vessel = data.vessel;
    for (var i = 0; i < report_data.route.length; i++) {
        report_data.speed_freeboard.push({ speed: report_data.route[i].speed.knots, freeboard: report_data.route[i].freeboard, date: report_data.route[i].date_reached });
        report_data.risk.risks.push({ string: report_data.route[i].risk_string, date: report_data.route[i].date_reached });
        report_data.risk.incident_likelihood.push({ number: report_data.route[i].overall_threat, date: report_data.route[i].date_reached });

        report_data.risk.incidents_types.push({
            date: report_data.route[i].date_reached, percentages: {
                suspicious_percentage: report_data.route[i].suspicious_percentage,
                approached_percentage: report_data.route[i].approached_percentage, fired_upon_percentage: report_data.route[i].fired_upon_percentage, hijacking_kidnapping_percentage: report_data.route[i].hijacking_kidnapping_percentage,
                boarded_percentage: report_data.route[i].boarded_percentage, robbed_percentage: report_data.route[i].robbed_percentage
            }
        });

        if (i != 0) {
            const sea_list = report_data.hours_in.find(item => item.name === "Hours spent in Seas");
            const eez_list = report_data.hours_in.find(item => item.name === "Hours spent in EEZs");
            const jwc_list = report_data.hours_in.find(item => item.name === "Hours spent in JWCs");
            if(normaliseZoneName(sea_list?.info.slice(-1)[0]?.name) !== normaliseZoneName(report_data.route[i].sea_name)) {
                sea_list.info.push({ name: report_data.route[i].sea_name, date: report_data.route[i].date_reached });
            }
            if (normaliseZoneName(eez_list?.info.slice(-1)[0]?.name) !== normaliseZoneName(report_data.route[i].eez_name)) {
                eez_list.info.push({ name: report_data.route[i].eez_name, date: report_data.route[i].date_reached });
            }
            if (normaliseZoneName(jwc_list?.info.slice(-1)[0]?.name) !== normaliseZoneName(report_data.route[i].jwc_name)) {
                jwc_list.info.push({ name: report_data.route[i].jwc_name, date: report_data.route[i].date_reached });
            }
        }
        else{
            const new_sea_entry: ReportObjects.hours_item_details = {
                name: report_data.route[i].sea_name, date: report_data.route[i].date_reached
            };
            const new_sea_item: ReportObjects.hours_item = { name: "Hours spent in Seas", info: [new_sea_entry] };
            const new_eez_entry: ReportObjects.hours_item_details = {
                name: report_data.route[i].eez_name, date: report_data.route[i].date_reached
            };
            const new_eez_item: ReportObjects.hours_item = { name: "Hours spent in EEZs", info: [new_eez_entry] };
            const new_jwc_entry: ReportObjects.hours_item_details = {
                name: report_data.route[i].jwc_name, date: report_data.route[i].date_reached
            };
            const new_jwc_item: ReportObjects.hours_item = { name: "Hours spent in JWCs", info: [new_jwc_entry] };

            report_data.hours_in.push(new_sea_item);
            report_data.hours_in.push(new_eez_item);
            report_data.hours_in.push(new_jwc_item);
        }
    }
    return report_data;
}
interface new_zone{
    name: string;
    hours: number;
}
interface zone_plot{
    x_value: [];
    y_value: [];
}

export function speed_plot(report_data) {
    const risks = report_data.speed_freeboard;

    const xValues = risks.map((risk) => risk.date);
    const yspeedValues = risks.map((risk) => risk.speed);
    const yfreeboardValues = risks.map((risk) => risk.freeboard);

    var trace1 = {
        x: xValues,
        y: yspeedValues,
        type: 'line',
        marker: { color: ReportObjects.line_plot_colours[0] },
        name: 'Speed'
    };

    var trace2 = {
        x: xValues,
        y: yfreeboardValues,
        type: 'line',
        marker: { color: ReportObjects.line_plot_colours[1] },
        name: 'Freeboard'
    };

    var layout = {
        autosize: false,
        autorange: false,
        yaxis: {
                range: [0, null]
            },
        width: 1100,
        height: 280,
        margin: { l: 10, r: 0, t: 20, b: 20, },
        font: { size: 8, color: '#000' },
    };
    var data = [trace1, trace2];

    const plot_object: ReportObjects.AllZonePlots = {
        plot_name: "Speed (kn) / Freeboard (m)",
        plot_data: { data, layout }
    }

    return plot_object;
}

export function risk_plot(report_data) {
    const risks = report_data.risk.risks;
    const xValues = risks.map((risk) => risk.date);
    //const yValues = risks.map((risk) => risk.string.trim());

    const riskMapping = {
      "LOW": 0,
      "MEDIUM-LOW": 1,
      "MEDIUM": 2,
      "MEDIUM-HIGH": 3,
      "HIGH": 4,
      "VERY-HIGH": 5,
      "EXTREME": 6,
    };

    const yValues = risks.map((risk) => riskMapping[risk.string.trim().toUpperCase()] || 0);

    var trace1 = {
        x: xValues,
        y: yValues,
        type: 'line'
    };
    var layout = {
        yaxis: {
            title: 'Risk',
            range: [0, 6],
            autorange: false,  // Disable autorange to use the specified range
            tickmode: 'array',
            tickvals: [0, null, null, null, null, null, 6],  // Map tick values to positions in the range
            ticktext: ["LOW", "", "", "", "", "", "EXTREME"],
            tickangle: -90,
        },
        autoscale: false,
        width: 540,
        height: 275,
        margin: { l: 20, r: 0, t: 30, b: 10, },
        font: { size: 8, color: '#000' },
    };
    var data = [trace1];

    const plot_object: ReportObjects.AllZonePlots = {
        plot_name: "Risk",
        plot_data: { data, layout }
    }
    return plot_object;
}

export function incidents_types(report_data) {
    const main_data = report_data.risk.incidents_types;
    const date_values = main_data.map(item => item.date);
    const percentage_types = Object.keys(main_data[0].percentages);

    const data = percentage_types.map(percentage_types => {
        const totalValues = main_data.map(item => percentage_types)
            .reduce((acc, val) => acc.map((el, i) => el + val[i]), new Array(date_values.length).fill(0));

        var type_name = percentage_types;
        var type_color = "#cccccc";

        if(ReportObjects.incident_plot_colours[percentage_types]){
            type_name = ReportObjects.incident_plot_colours[percentage_types][0];
            type_color = ReportObjects.incident_plot_colours[percentage_types][1];
        }

        return {
            x: date_values,
            y: main_data.map(item => item.percentages[percentage_types])
                .map((values, i) => values),
            type: 'scatter',
            mode: 'lines',
            stackgroup: 'one',
            name: type_name,
            marker: { color: type_color },
            groupnorm: 'percent'
        };
    });

    const layout = {
        xaxis: {
            showgrid: false,
            zeroline: false,
            visible: false,
        },
        yaxis: {
            showgrid: false,
            zeroline: false,
            visible: false,
        },
        font: { family: 'sans-serif', size: 8, color: '#000' },
        autosize: false,
        width: 1100,
        height: 280,
        margin: { l: 20, r: 0, t: 20, b: 20, },
    };
    const plot_object: ReportObjects.AllZonePlots = {
        plot_name: "Ratio of incident types along route",
        plot_data: { data, layout }
    }

    return plot_object;
}
export function incident_likelihood(report_data) {
     const incident_likelihood = report_data.risk.incident_likelihood;
     const xValues = incident_likelihood.map((incident_likelihood) => incident_likelihood.date);
     const yValues = incident_likelihood.map((incident_likelihood) => incident_likelihood.number);

     var trace1 = {
         x: xValues,
         y: yValues,
         type: 'line'
     };

     var layout = {
        yaxis: {
             range: [0, 1.1],
             title: 'Likelihood',
             tickmode: 'array',
             tickvals: [0, 1.1],
             ticktext: ["Low", "High"],
             tickangle: -90,
         },
        width: 1100,
        height: 280,
        margin: { l: 20, r: 0, t: 20, b: 20, },
        font: { size: 8, color: '#000' },
     };
     var data = [trace1];

     const plot_object: ReportObjects.AllZonePlots = {
         plot_name: "Incident Likelihood",
         plot_data: { data, layout }
     }

     return plot_object;
}

function zone_charts(input_data: ReportObjects.hours_item, transit_hours, eta): ReportObjects.AllZonePlots{
    var zone_hours: zone_plot = {
        x_value: [],
        y_value: []
    };
    if (input_data.info.length > 1) {
        for (var i = 0; i < input_data.info.length; i++) {
            const existingZoneIndex = zone_hours.x_value.findIndex(name => normaliseZoneName(name) === normaliseZoneName(input_data.info[i].name));
            var hour = 0;
            if( i != input_data.info.length - 1 ) {
                hour = Math.abs((new Date(input_data.info[i + 1].date) - new Date(input_data.info[i].date)) / (1000 * 60 * 60));
            }
            else {
                hour = Math.abs((new Date(eta) - new Date(input_data.info[i].date)) / (1000 * 60 * 60));
            }
            if (existingZoneIndex === -1) {
                zone_hours.x_value.push(input_data.info[i].name);
                zone_hours.y_value.push(hour);
            } else {
                zone_hours.y_value[existingZoneIndex] += hour;
            }
        }
    }
    else if(input_data.info[0] != null && input_data.info[0].name != null){
        zone_hours.x_value.push(input_data.info[0].name);
        zone_hours.y_value.push(transit_hours);
    }

    var data = [];

    for (var i = 0; i < zone_hours.x_value.length; i++) {
        var trace = {
            y: [tidyZoneLabel(zone_hours.x_value[i])],
            x: [zone_hours.y_value[i]],
            text: [tidyZoneLabel(zone_hours.x_value[i])],
            textposition: "outside",
            type: 'bar',
            name: zone_hours.x_value[i],
            orientation: 'h',
            marker: { color: ReportObjects.color_palette[i] }
        };

        data.push(trace);
    }

    data.sort((a, b) => a.x[0] - b.x[0]);
    if(data.length >= 1){
        const max_value = data[data.length - 1].x[0] * 1.1;
        const layout = {
            font: { size: 8, color: '#000' },
            margin: { l: 0, r:0, t: 20, b: 20, },
            yaxis: { side:'right', visible:false},
            xaxis: {range: [0, max_value]},
            showlegend: false,
            width: 1100,
            height: 280,
            bargap: 0,
            bargroupgap: 0
        };

        const plot_object: ReportObjects.AllZonePlots = {
            plot_name: input_data.name.replace("EEZs", "Exclusive Economic Zones"),
            plot_data: { data, layout },
        };

        return plot_object;
    }
    else{
        return null;
    }
}

export function generateZoneCharts(data: ReportObjects.report_info): ReportObjects.AllZonePlots[] {
    var plots: ReportObjects.AllZonePlots[] = [];
    for(var i = 0; i < data.hours_in.length; i++){
        if(data.hours_in[i] != null){
            const isExistingPlot = plots.some(plot => plot.plot_name === data.hours_in[i].name);

            if (!isExistingPlot) {
                var plot_object: ReportObjects.AllZonePlots = zone_charts(data.hours_in[i], data.transit.hours, data.transit.eta);
                if(plot_object != null) plots.push(plot_object);
            }
        }
    }
    plots = plots.filter((plot, index, self) =>
        index === self.findIndex(p => p.plot_name === plot.plot_name)
    );
    return plots;
}

export function generate_report_route(report_data, report_map) {
try{
    var batch_size = 10000;
    var path_data = {};
    var source_layers = [];
    if ('route' in report_data) { path_data = { "Waypoints": report_data.route } }
    else if(report_data){ path_data = { "Waypoints": report_data } }

    if (path_data == null) return false;
    //console.log("path_data.Waypoints: ", path_data.Waypoints.length);
    path_data.Waypoints.reverse();
    var map_bounds = [[200, 200], [200, 200]];
    var batchTotal = Math.ceil(path_data.Waypoints.length/batch_size);
    if(batchTotal < 1) batchTotal = 1;

    for (var j = 0; j < batchTotal; j++){
        var geojson_line = {
            "type": "FeatureCollection", "features": []
        };
        var geojson_point = {
            "type": "FeatureCollection", "features": []
        };
        var empty_position = { 'type': 'Feature', 'properties': {}, 'geometry': { 'coordinates': [], 'type': null } };
        var current_i = j * batch_size;
        var current_total = Math.min(current_i + batch_size + 1, path_data.Waypoints.length);

        if(current_i > 0) current_i = current_i - 1
        const colors = [
            "#e6194b", "#3cb44b", "#ffe119", "#4363d8", "#f58231", "#911eb4",
            "#e6194b", "#3cb44b", "#ffe119", "#4363d8", "#f58231", "#911eb4",
            "#e6194b", "#3cb44b", "#ffe119", "#4363d8", "#f58231", "#911eb4",
            "#e6194b", "#3cb44b", "#ffe119", "#4363d8", "#f58231", "#911eb4",
            "#e6194b", "#3cb44b", "#ffe119", "#4363d8", "#f58231", "#911eb4",
        ];
        var colour_temp = colors[j];
        for (var i = current_i; i < current_total; i++) {
            if(i >= path_data.Waypoints.length) break;
            var coordinates = path_data.Waypoints[i]?.position_location?.dec || path_data.Waypoints[i].dec || null;
            if(i < (j * batch_size)) {
                //console.log("coordinates: ", coordinates)
                coordinates["lon"] = coordinates["lon"] + 1;
                coordinates["lat"] = coordinates["lat"] + 1;
                //console.log("changed coordinates: ", coordinates)
            }
            if(coordinates == null) continue;
            if (coordinates["lon"] < map_bounds[0][0] || map_bounds[0][0] === 200) {
                map_bounds[0][0] = coordinates["lon"];
            }
            if (coordinates["lon"] > map_bounds[0][1] || map_bounds[0][1] === 200) {
                map_bounds[0][1] = coordinates["lon"];
            }
            if (coordinates["lat"] < map_bounds[1][0] || map_bounds[1][0] === 200) {
                map_bounds[1][0] = coordinates["lat"];
            }
            if (coordinates["lat"] > map_bounds[1][1] || map_bounds[1][1] === 200) {
                map_bounds[1][1] = coordinates["lat"];
            }

            var new_point = JSON.parse(JSON.stringify(empty_position));
            var new_line = JSON.parse(JSON.stringify(empty_position));

            new_point.properties.lat = parseFloat(coordinates["lon"]);
            new_point.properties.lng = parseFloat(coordinates["lat"]);
            new_line.properties.colour = path_data.Waypoints[i].risk_colour_hex || "#4285F4";
            //new_line.properties.colour = colour_temp;

            if (i < path_data.Waypoints.length - 1) {
                var next_position =  path_data.Waypoints[i + 1]?.position_location?.dec || path_data.Waypoints[i+1].dec
                var p1 = new LatLon(parseFloat(next_position["lon"]), parseFloat(next_position["lat"]));
                var p2 = new LatLon(parseFloat(coordinates["lon"]), parseFloat(coordinates["lat"]));

                new_point.properties.heading = p1.bearingTo(p2);
                new_line.properties.heading = p1.bearingTo(p2);
                new_point.geometry.coordinates = [parseFloat(coordinates["lon"]), parseFloat(coordinates["lat"])];

                var start = { x: parseFloat(coordinates["lon"]), y: parseFloat(coordinates["lat"]) };
                var end = { x: parseFloat(next_position["lon"]), y: parseFloat(next_position["lat"]) };
                var generator = new arc.GreatCircle(start, end, { 'name': 'RoutePath' });

                const distance = p1.distanceTo(p2); // in km
                const steps = Math.min(50, Math.max(5, Math.floor(distance * 1.5)));
                var line = generator.Arc(steps, { offset: 10 });
                var geojson = line.json();

                geojson.properties = new_line.properties;
                new_point.geometry.type = "Point";
                geojson_point.features.push(new_point);

                geojson_line.features.push(geojson);
            }else {
                var prev_position =  path_data.Waypoints[i - 1]?.position_location?.dec || path_data.Waypoints[i - 1].dec
                var p1 = new LatLon(parseFloat(coordinates["lon"]), parseFloat(coordinates["lat"]));
                var p2 = new LatLon(parseFloat(prev_position["lon"]), parseFloat(prev_position["lat"]));

                new_point.properties.heading = p1.bearingTo(p2);
                new_point.geometry.coordinates = [parseFloat(coordinates["lon"]), parseFloat(coordinates["lat"])];
                new_point.geometry.type = "Point";

                geojson_point.features.push(new_point);
             }
        }
        //console.log("geojson_line: ", geojson_line.features.length)
        //console.log("geojson_point: ", geojson_point.features.length)

        var simplifiedLine = turf.simplify(geojson_line, { tolerance: 0.1, highQuality: false, mutate: true });
        simplifiedLine = turf.truncate(simplifiedLine, { precision: 3 });

        const sourceId = `MTPathLine-${j}`;
        const layerId = `MTPathLine-${j}`;
        source_layers.push({ "simplifiedLine" : simplifiedLine, "sourceId" : sourceId, "layerId" : layerId});
    }
    for (var i = 0; i < source_layers.length; i++){
        const source = report_map?.getSource(source_layers[i].sourceId);
        if (source) {
            const mergedLineData = {
                type: "FeatureCollection",
                features: source._data.features.concat(source_layers[i].simplifiedLine.features)
            };
            source.setData(mergedLineData);
        } else {
            report_map?.addSource(source_layers[i].sourceId, {
                type: 'geojson', data: source_layers[i].simplifiedLine
            });
        }

        if (report_map) {
            if (!report_map.getLayer(source_layers[i].layerId)) {
                const layerDef = overlayDefintions.find(x => x.id === "MTPathLine");
                if (layerDef) {
                    const layerCopy = JSON.parse(JSON.stringify(layerDef));
                    layerCopy.id = source_layers[i].layerId;
                    layerCopy.source = source_layers[i].sourceId;

                    report_map.addLayer(layerCopy);
                }
            }
            const offset = (i % 2 === 1) ? 0.2 : 0;  // 2 pixels for odd layers, else 0
            if (report_map.getLayer(source_layers[i].layerId)) {
                report_map.setPaintProperty(source_layers[i].layerId, 'line-offset', offset);
            }

            const bounds = new mapboxgl.LngLatBounds(
                new mapboxgl.LngLat(map_bounds[0][0], map_bounds[1][0]),
                new mapboxgl.LngLat(map_bounds[0][1], map_bounds[1][1])
            );
            report_map.fitBounds(bounds, {
                padding: 20,
            });
        }
    }
        path_data.Waypoints.reverse();
    return true;
}catch(ex){
    console.error(ex);
    return false;
}
}


export async function add_markers(report_map, markerList, closePort,  doBounds = true) {
    try{
        const features = markerList.map((marker, index) => ({
            type: "Feature",
            geometry: {
                type: "Point",
                coordinates: [marker.dec.lon, marker.dec.lat] // Assuming marker has { lng, lat }
            },
            properties: {
                  ...(markerList.length > 1 && { label: marker.id })
            }
        }));

        if (report_map && report_map.getSource("MTPoint")) {
            report_map.getSource("MTPoint").setData({
                type: "FeatureCollection",
                features: features
            });
        } else if(report_map) {
            report_map.addSource("MTPoint", {
                type: "geojson",
                data: {
                    type: "FeatureCollection",
                    features: features
                }
            });
            var circle_radius = 8;
            if(markerList.length == 1) circle_radius = 10;
            report_map.addLayer({
                id: "MTPointLayer",
                type: "circle",
                source: "MTPoint",
                paint: {
                    "circle-radius": circle_radius,
                    "circle-color": "#EA3323",
                    "circle-stroke-width": 2,
                    "circle-stroke-color": "#FFFF00"
                }
            });
            if(markerList.length > 1) {
                report_map.addLayer({
                    id: "MTPointLabels",
                    type: "symbol",
                    source: "MTPoint",
                    layout: {
                        'text-field': ['get', 'label'],
                        'text-font': ['Roboto Bold', 'Open Sans Bold', 'Arial Unicode MS Bold'],
                        'text-size': 14,
                        'text-offset': [0, 1.5],
                        'text-allow-overlap': true
                    },
                    paint: {
                        'text-color': '#000'
                    }
                });
            }
        }

        const routeSource = report_map?.getSource("MTPathLine");
        if (markerList.length > 0 && report_map && !routeSource) {
            const bounds = new mapboxgl.LngLatBounds();
            if(closePort != null && closePort.lon) {
                bounds.extend([closePort.lon, closePort.lat])
            }
            markerList.forEach(marker => bounds.extend([marker.dec.lon, marker.dec.lat]));

            report_map.fitBounds(bounds, {
                padding: 20
            });
        }
    }catch(ex){
        console.error(ex);
    }
}

function page_break_generator(source){
    var footer_height = document.getElementById("report_footer").offsetHeight;
    var header_height = document.getElementById("report_header").offsetHeight;
    var page_height = G.page_size[0][1];
    if(G.is_portrait) page_height = G.page_size[1][1];

    var content_height = page_height - (footer_height + header_height);
    var current_height = 0;
    var new_source = [];
    setAllPages(1)
    for(var i = 0; i < source.length;i++){
        if(!source[i].querySelector('page_break')){
            current_height += source[i].offsetHeight;
        }

        if((current_height > content_height  && i != source.length - 1  && !source[i+1].querySelector('.page_break')) ||
            current_height > content_height  && i === source.length - 1){
            current_height = source[i].offsetHeight;
            setAllPages(1, true);
            insertBreak(source[i]);
        }else if(i != source.length - 1 && source[i+1].querySelector('.page_break')){
            current_height = 0;
            new_source.push(source[i]);
            setAllPages(1, true);
        }
        else {
            new_source.push(source[i]);
        }
    }
    source = new_source;
}

function insertBreak(reference_node) {
    var new_div = document.createElement('div');
    new_div.className = 'report_row';
    var page_break_div = document.createElement('div');
    page_break_div.className = 'page_break';
    new_div.appendChild(page_break_div);

    reference_node.parentNode.insertBefore(new_div, reference_node);
}

export function gather_source(container_ids, container_classes, action, map_ref, portrait = false) {
    let source = Array.from(document.querySelectorAll(".report_row"));

    const extractRows = (elements) => {
        elements.forEach((element) => {
            const mapDiv = element.querySelector("#map_custom_cont");
            if (mapDiv) mapDiv.remove();
            source.push(...element.querySelectorAll(".report_row"));
        });
    };

    if (container_ids.length) extractRows(container_ids.map(id => document.getElementById(id)).filter(Boolean));
    if (container_classes.length) extractRows([...document.querySelectorAll(container_classes.map(cls => `.${cls}`).join(","))]);

    source = source.sort((a, b) => a.getBoundingClientRect().top + window.scrollY - (b.getBoundingClientRect().top + window.scrollY));

    if (action === "page_break") {
        page_break_generator(source);
    } else if (action.includes("download")) {
        logReportUsage(action); // 'download_pdf' | 'download_word' | 'download_excel'
        document.getElementById("downloading").style.display = "block";
        calc_table_pages();
        try{
            map_conversion_handler(map_ref.current);
        }
        catch(e){
            console.error(e)
        }
        generate_pdf(source, map_ref, action, portrait);
    }

    if (ReportObjects.host_name !== "Maritrace") {
        document.querySelectorAll(".user_custom_container").forEach(element => {
            try {
                element.remove();
            } catch (e) {
                console.warn("Failed to remove element:", element, e);
            }
        });
    }
}

function calc_table_pages(){
    var table = document.querySelector('.detailed_class');
    if(table){
        var rows = table.querySelectorAll('tr');
        const num_pages = Math.ceil(rows.length / 36);
        setAllPages(num_pages, true);
    }
}

function PlotComponent({ plotData, title, plot_only = false}) {
  if(plot_only) {
      return ( <div className="convert_to_image plot_type_1">
        <Plot
          data={plotData.plot_data.data}
          layout={plotData.plot_data.layout}
          useResizeHandler={true}
            config={{displaylogo: false}}
          style={{ width: '100%', height: '100%' }}
        />
      </div>)
  }
  else {
  return (
    <div className={plotData.plot_name != "All Incidents" ? "report_row" : undefined} key={plotData.plot_name}>
      <div className="inner_padding">
        <p className="sub_title">{title || plotData.plot_name}</p>
        <div className="convert_to_image plot_type_1">
          <Plot
            data={plotData.plot_data.data}
            layout={plotData.plot_data.layout}
            useResizeHandler={true}
            config={{displaylogo: false}}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      </div>
    </div>
  );
  }
}

export function ReportPlot({ plotData, title, errorMessage, plot_only = false }) {
  return plotData && plotData.plot_name ? (
    <PlotComponent plotData={plotData} title={title} plot_only={plot_only}/>
  ) : (
    <p>{errorMessage}</p>
  );
}

function lat_cross_check(lat1, lat2) {
    return (lat1 > 0 && lat2 < 0 || lat1 < 0 && lat2 > 0) && Math.abs(lat1 - lat2) > 90;
}
function lon_cross_check(lon1, lon2) {
    return (lon1 > 0 && lon2 < 0 || lon1 < 0 && lon2 > 0) && Math.abs(lon1 - lon2) > 180;
}
